import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Zap } from "lucide-react";
import { fmtMoney, fmtDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { orderBuyToClose, orderSellToOpen } from "../lib/api";
import { pollOrderStatus } from "../lib/orderStatus";
import { TradeConfirmDialog } from "./TradeConfirmDialog";

export const PositionTracker = ({
  positions,
  tickers,
  chains,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
  onPickTicker,
  onAddCustomTicker,
  strategy,
}) => {
  const { user, refresh } = useAuth();
  const [confirmClose, setConfirmClose] = useState(null); // position to buy-back
  const [closing, setClosing] = useState(false);
  const [confirmSTO, setConfirmSTO] = useState(null); // pending sell-to-open payload
  const [stoing, setStoing] = useState(false);
  const [stoOnSubmit, setStoOnSubmit] = useState(false);
  const tradingReady = user?.trading_enabled && user?.tradier_connected;
  const strategyKey = strategy?.key || "short_call";
  const isShortStrategy = strategyKey === "short_call" || strategyKey === "short_put";
  const isPutStrategy = strategyKey === "short_put" || strategyKey === "long_put";
  const suffix = isPutStrategy ? "P" : "C";
  const [open, setOpen] = useState(false);
  const [customSym, setCustomSym] = useState("");
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [form, setForm] = useState({
    symbol: tickers[0]?.symbol || "AAPL",
    strike: "",
    expiration: "",
    contracts: 1,
    premium_received: "",
  });

  const chain = chains[form.symbol]?.options || [];
  const expirations = [...new Set(chain.map((o) => o.expiration))];
  const strikesForExp = [...new Set(chain.filter((o) => o.expiration === form.expiration).map((o) => o.strike))].sort((a, b) => a - b);

  // Current live mid for the selected strike + expiration — pre-fills the
  // premium field on selection change so users placing a fresh covered call
  // don't have to look up the quote. Backfilling users can just type over it.
  const selectedContract = chain.find(
    (o) => o.expiration === form.expiration && Number(o.strike) === Number(form.strike)
  );
  const currentMid = selectedContract?.mid ?? null;

  React.useEffect(() => {
    // When the user changes strike / expiration, auto-fill premium with the
    // current live mid — but only if the user hasn't manually typed something
    // in already (we don't want to clobber their edits on every dropdown click).
    if (currentMid != null && !form._premium_edited) {
      setForm((f) => ({ ...f, premium_received: currentMid.toFixed(2) }));
    }
  }, [currentMid]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.strike || !form.expiration) return;
    const payload = {
      symbol: form.symbol,
      strike: Number(form.strike),
      expiration: form.expiration,
      contracts: Number(form.contracts),
      premium_received: form.premium_received === "" ? null : Number(form.premium_received),
    };
    // If user asked to also route to Tradier, confirm first. Otherwise save immediately.
    if (stoOnSubmit && tradingReady) {
      setConfirmSTO(payload);
      return;
    }
    await onAdd(payload);
    setForm({ ...form, strike: "", expiration: "", premium_received: "", _premium_edited: false });
    setOpen(false);
  };

  const commitSTO = async () => {
    if (!confirmSTO) return;
    setStoing(true);
    try {
      // 1. Save into tracker first so a Tradier fill has a matching local row
      await onAdd(confirmSTO);
      // 2. Submit sell-to-open to Tradier
      const r = await orderSellToOpen({
        symbol: confirmSTO.symbol,
        strike: confirmSTO.strike,
        expiration: confirmSTO.expiration,
        contracts: confirmSTO.contracts,
        strategy: strategyKey,
        limit_price: confirmSTO.premium_received != null && confirmSTO.premium_received > 0
          ? Number(confirmSTO.premium_received.toFixed?.(2) ?? confirmSTO.premium_received)
          : null,
      });
      toast.success(
        r.mode === "live"
          ? `Position saved · submitted to Tradier · order ${r.tradier_order_id || "queued"}`
          : "Position saved · SIMULATE mode — no Tradier order was sent. Flip header toggle to SUBMIT for real orders."
      );
      if (r.mode === "live" && r.order_id) {
        pollOrderStatus({
          orderId: r.order_id,
          onUpdate: (s) => {
            if (["filled", "canceled", "cancelled", "rejected", "expired"].includes(String(s.status).toLowerCase())) {
              toast[s.status === "filled" ? "success" : "warning"](`STO ${s.status}${s.avg_fill_price ? ` @ $${Number(s.avg_fill_price).toFixed(2)}` : ""}`);
            }
          },
        });
      }
      setConfirmSTO(null);
      setStoOnSubmit(false);
      setForm({ ...form, strike: "", expiration: "", premium_received: "", _premium_edited: false });
      setOpen(false);
      await refresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? String(detail).slice(0, 300) : "Tradier submit failed — position saved locally only", { duration: 12000 });
    } finally { setStoing(false); }
  };

  return (
    <div className="panel" data-testid="position-tracker-panel">
      <div className="panel-h">
        <div>
          <div className="label-cap">Position Tracker</div>
          <div className="text-xs text-[#71717A] mt-1 font-mono">
            {positions.length} OPEN
          </div>
        </div>
        <button
          data-testid="add-position-toggle-btn"
          onClick={() => setOpen((v) => !v)}
          className="btn-outline flex items-center gap-1.5"
        >
          <Plus size={14} /> {open ? "Cancel" : "Add"}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="p-4 border-b border-[#27272A] space-y-3" data-testid="add-position-form">
          <div>
            <label className="label-cap block mb-1.5">Add Custom Ticker</label>
            <div className="flex gap-2">
              <input
                data-testid="custom-ticker-input"
                value={customSym}
                onChange={(e) => setCustomSym(e.target.value.toUpperCase())}
                placeholder="e.g. QCOM"
                maxLength={6}
                className="flex-1 bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm font-mono uppercase focus:border-[#007AFF] outline-none"
              />
              <button
                data-testid="custom-ticker-load-btn"
                type="button"
                disabled={!customSym || loadingCustom}
                onClick={async () => {
                  if (!customSym) return;
                  setLoadingCustom(true);
                  const ok = await onAddCustomTicker(customSym);
                  setLoadingCustom(false);
                  if (ok) {
                    setForm((f) => ({ ...f, symbol: customSym, strike: "", expiration: "" }));
                    setCustomSym("");
                  }
                }}
                className="btn-outline text-xs px-3"
              >
                {loadingCustom ? "..." : "Load"}
              </button>
            </div>
          </div>
          <div>
            <label className="label-cap block mb-1.5">Ticker</label>
            <select
              data-testid="form-ticker-select"
              value={form.symbol}
              onChange={(e) => {
                setForm({ ...form, symbol: e.target.value, strike: "", expiration: "" });
                onPickTicker(e.target.value);
              }}
              className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#007AFF] outline-none"
            >
              {tickers.map((t) => (
                <option key={t.symbol} value={t.symbol}>
                  {t.symbol} — {fmtMoney(t.price)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-cap block mb-1.5">Expiration</label>
            <select
              data-testid="form-exp-select"
              value={form.expiration}
              onChange={(e) => setForm({ ...form, expiration: e.target.value, strike: "" })}
              className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#007AFF] outline-none"
            >
              <option value="">Select…</option>
              {expirations.map((e) => (
                <option key={e} value={e}>
                  {fmtDate(e)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-cap block mb-1.5">Strike</label>
            <select
              data-testid="form-strike-select"
              value={form.strike}
              onChange={(e) => setForm({ ...form, strike: e.target.value })}
              disabled={!form.expiration}
              className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#007AFF] outline-none disabled:opacity-40"
            >
              <option value="">Select…</option>
              {strikesForExp.map((s) => (
                <option key={s} value={s}>
                  ${s.toFixed(2)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-cap block mb-1.5">Contracts</label>
            <input
              data-testid="form-contracts-input"
              type="number"
              min="1"
              value={form.contracts}
              onChange={(e) => setForm({ ...form, contracts: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#007AFF] outline-none"
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <label className="label-cap">Premium received per share <span className="text-[#71717A] normal-case">(optional)</span></label>
              {currentMid != null && (
                <button
                  data-testid="form-premium-use-mid-btn"
                  type="button"
                  onClick={() =>
                    setForm({ ...form, premium_received: currentMid.toFixed(2), _premium_edited: false })
                  }
                  className="text-[10px] font-mono text-[#00E676] hover:text-white transition-colors"
                  title="Fill with current live option mid price"
                >
                  Current mid ${currentMid.toFixed(2)} · use
                </button>
              )}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A] font-mono text-sm">$</span>
              <input
                data-testid="form-premium-input"
                type="number"
                step="0.01"
                min="0"
                value={form.premium_received}
                onChange={(e) => setForm({ ...form, premium_received: e.target.value, _premium_edited: true })}
                placeholder={currentMid != null ? currentMid.toFixed(2) : "e.g. 1.45"}
                className="w-full bg-[#0A0A0A] border border-[#27272A] pl-7 pr-3 py-2 text-sm font-mono focus:border-[#007AFF] outline-none"
              />
            </div>
          </div>
          <p className="text-[10px] text-[#71717A] leading-relaxed">
            Auto-filled with the current live mid quote. If you&rsquo;re backfilling an older position,
            just type over it with the premium you actually collected. Leave blank if unknown.
          </p>
          {tradingReady && (
            <label className="flex items-start gap-2 text-[11px] cursor-pointer group" data-testid="form-sto-checkbox-wrap">
              <input
                type="checkbox"
                checked={stoOnSubmit}
                onChange={(e) => setStoOnSubmit(e.target.checked)}
                data-testid="form-sto-checkbox"
                className={`mt-0.5 ${user.trade_mode === "live" ? "accent-[#FF3B30]" : "accent-[#00E676]"}`}
              />
              <span className="flex-1">
                <span className={`font-bold uppercase tracking-widest text-[10px] ${
                  user.trade_mode === "live" ? "text-[#FF3B30]" : "text-[#00E676]"
                }`}>
                  {(() => {
                    const opener = isShortStrategy ? "sell-to-open" : "buy-to-open";
                    return user.trade_mode === "live"
                      ? `Also submit ${opener} to Tradier · ${(user.tradier_env || "").toUpperCase()}`
                      : `Also simulate ${opener} (no Tradier call)`;
                  })()}
                </span>
                <span className="block text-[10px] text-[#71717A] mt-0.5">
                  {user.trade_mode === "live"
                    ? "Real order routed to your Tradier account after saving."
                    : "Nothing hits Tradier — flip header toggle to SUBMIT for real orders."}
                </span>
              </span>
            </label>
          )}
          <button data-testid="form-submit-btn" type="submit" className="btn-accent w-full">
            {stoOnSubmit && tradingReady
              ? user.trade_mode === "live"
                ? "Save & Submit to Tradier"
                : `Save & Simulate ${isShortStrategy ? "STO" : "BTO"}`
              : "Save Position"}
          </button>
        </form>
      )}

      <div className="divide-y divide-[#27272A] max-h-[520px] overflow-y-auto">
        {positions.length === 0 && (
          <div className="p-6 text-center text-xs text-[#71717A]">
            No positions yet. Add a covered call to get rolling recommendations.
          </div>
        )}
        {positions.map((p) => {
          const active = p.id === selectedId;
          return (
            <div
              key={p.id}
              data-testid={`position-row-${p.id}`}
              onClick={() => onSelect(p.id)}
              className={`p-3 cursor-pointer transition-colors ${
                active ? "bg-[rgba(0,122,255,0.10)] border-l-2 border-[#007AFF]" : "hover:bg-[#1A1A1A] border-l-2 border-transparent"
              }`}
            >
              <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="font-bold text-sm tracking-tight">{p.symbol}</span>
                  <span className="font-mono text-sm">${p.strike.toFixed(2)}{(p.strategy === "short_put" || p.strategy === "long_put") ? "P" : "C"}</span>
                </div>
                <div className="flex items-center gap-1">
                  {tradingReady && (() => {
                    const posIsLong = p.strategy === "long_call" || p.strategy === "long_put";
                    const closeLabel = posIsLong ? "STC" : "BTC";
                    return (
                      <button
                        data-testid={`buy-back-${p.id}`}
                        onClick={(e) => { e.stopPropagation(); setConfirmClose(p); }}
                        title={
                          user.trade_mode === "live"
                            ? `Submit ${closeLabel} to Tradier (${user.tradier_env})`
                            : `Simulate ${closeLabel} — no Tradier call`
                        }
                        className={`px-1.5 py-0.5 border text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 transition-colors ${
                          user.trade_mode === "live"
                            ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)]"
                            : "border-[#00E676] text-[#00E676] hover:bg-[rgba(0,230,118,0.1)]"
                        }`}
                      >
                        <Zap size={9} /> {closeLabel}
                      </button>
                    );
                  })()}
                  <button
                    data-testid={`delete-position-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(p.id);
                    }}
                    className="btn-ghost-danger"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
              <div className="mt-1 flex items-center justify-between text-[11px] font-mono text-[#A1A1AA]">
                {(() => {
                  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(p.expiration);
                  const exp = m
                    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
                    : new Date(p.expiration);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dte = Math.round((exp - today) / 86400000);
                  const isExpired = dte < 0;
                  const isExpiringToday = dte === 0;
                  const color = isExpired
                    ? "#71717A"
                    : dte <= 7
                    ? "#FF3B30"
                    : dte <= 21
                    ? "#FF9F0A"
                    : "#00E676";
                  return (
                    <span
                      style={{ color }}
                      title={isExpired ? "This option has expired" : `${dte}d to expiration`}
                      data-testid={`pos-exp-${p.id}`}
                      className="flex items-center gap-1.5"
                    >
                      <span className={isExpired ? "line-through opacity-60" : ""}>
                        {fmtDate(p.expiration)} · {dte}d
                      </span>
                      {isExpired && (
                        <span
                          data-testid={`pos-expired-badge-${p.id}`}
                          className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-[1px] border"
                          style={{ color: "#FF3B30", borderColor: "#FF3B30" }}
                        >
                          Expired
                        </span>
                      )}
                      {isExpiringToday && (
                        <span
                          data-testid={`pos-expiring-today-badge-${p.id}`}
                          className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-[1px] border"
                          style={{ color: "#FF9F0A", borderColor: "#FF9F0A" }}
                        >
                          Expires today
                        </span>
                      )}
                    </span>
                  );
                })()}
                <span className="flex items-center gap-2">
                  {(() => {
                    const t = tickers.find((tt) => tt.symbol === p.symbol);
                    if (!t || !t.price) return null;
                    const otm = p.strike >= t.price;
                    return (
                      <span
                        className="text-[10px]"
                        style={{ color: otm ? "#00E676" : "#FF9F0A" }}
                        title="Current underlying price"
                        data-testid={`pos-underlying-${p.id}`}
                      >
                        ${t.price.toFixed(2)}
                      </span>
                    );
                  })()}
                  <span>×{p.contracts}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <TradeConfirmDialog
        open={!!confirmClose}
        mode={user?.trade_mode || "paper"}
        title={confirmClose && (confirmClose.strategy === "long_call" || confirmClose.strategy === "long_put") ? "Sell to Close" : "Buy to Close"}
        summary={confirmClose && (() => {
          const closeSide = (confirmClose.strategy === "long_call" || confirmClose.strategy === "long_put") ? "Sell" : "Buy back";
          const cSuffix = (confirmClose.strategy === "short_put" || confirmClose.strategy === "long_put") ? "P" : "C";
          return (
            <>
              {closeSide} <span className="font-bold text-white">{confirmClose.symbol}</span>{" "}
              <span className="text-white">${confirmClose.strike.toFixed(2)}{cSuffix}</span>{" "}
              exp <span className="text-white">{fmtDate(confirmClose.expiration)}</span>.
            </>
          );
        })()}
        details={confirmClose ? (() => {
          const isLong = confirmClose.strategy === "long_call" || confirmClose.strategy === "long_put";
          const cSuffix = (confirmClose.strategy === "short_put" || confirmClose.strategy === "long_put") ? "P" : "C";
          return [
            { label: "Symbol", value: confirmClose.symbol },
            { label: "Strike", value: `$${confirmClose.strike.toFixed(2)}${cSuffix}` },
            { label: "Expiration", value: confirmClose.expiration },
            { label: "Contracts", value: confirmClose.contracts || 1 },
            { label: "Side", value: isLong ? "SELL_TO_CLOSE" : "BUY_TO_CLOSE" },
            { label: "Order type", value: "MARKET" },
          ];
        })() : []}
        busy={closing}
        onCancel={() => !closing && setConfirmClose(null)}
        onConfirm={async () => {
          if (!confirmClose) return;
          setClosing(true);
          try {
            const r = await orderBuyToClose({ position_id: confirmClose.id });
            toast.success(
              r.mode === "live"
                ? `Submitted to Tradier · order ${r.tradier_order_id || "queued"}`
                : "SIMULATE mode — no order was sent to Tradier. Flip header toggle to SUBMIT for real orders."
            );
            if (r.mode === "live" && r.order_id) {
              pollOrderStatus({
                orderId: r.order_id,
                onUpdate: (s) => {
                  if (["filled", "canceled", "cancelled", "rejected", "expired"].includes(String(s.status).toLowerCase())) {
                    toast[s.status === "filled" ? "success" : "warning"](`BTC ${s.status}${s.avg_fill_price ? ` @ $${Number(s.avg_fill_price).toFixed(2)}` : ""}`);
                  }
                },
              });
            }
            setConfirmClose(null);
            await refresh?.();
          } catch (e) {
            const detail = e?.response?.data?.detail;
            toast.error(detail ? String(detail).slice(0, 300) : "Buy-to-close failed", { duration: 12000 });
          } finally { setClosing(false); }
        }}
      />
      <TradeConfirmDialog
        open={!!confirmSTO}
        mode={user?.trade_mode || "paper"}
        title={isShortStrategy ? "Sell to Open" : "Buy to Open"}
        summary={confirmSTO && (
          <>
            {isShortStrategy ? "Sell" : "Buy"} <span className="font-bold text-white">{confirmSTO.symbol}</span>{" "}
            <span className="text-white">${confirmSTO.strike.toFixed(2)}{suffix}</span>{" "}
            exp <span className="text-white">{fmtDate(confirmSTO.expiration)}</span>
            {confirmSTO.premium_received != null && confirmSTO.premium_received > 0 && (
              <> at <span className="text-white">${confirmSTO.premium_received.toFixed(2)}</span> limit</>
            )}.
          </>
        )}
        details={confirmSTO ? [
          { label: "Symbol", value: confirmSTO.symbol },
          { label: "Strike", value: `$${confirmSTO.strike.toFixed(2)}${suffix}` },
          { label: "Expiration", value: confirmSTO.expiration },
          { label: "Contracts", value: confirmSTO.contracts },
          { label: "Side", value: isShortStrategy ? "SELL_TO_OPEN" : "BUY_TO_OPEN" },
          { label: "Order type", value: (confirmSTO.premium_received != null && confirmSTO.premium_received > 0) ? "LIMIT" : "MARKET" },
        ] : []}
        busy={stoing}
        onCancel={() => !stoing && setConfirmSTO(null)}
        onConfirm={commitSTO}
      />
    </div>
  );
};
